import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiInfo } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 0.95rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 0.85rem;
  color: ${props => props.warning ? '#ff6b6b' : '#666'};
  margin-top: 0.5rem;
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #007bff;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #0056b3;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

const PreviewSection = styled.div`
  margin-top: 2rem;
`;

const PreviewTitle = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PreviewBox = styled.div`
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
`;

const PreviewText = styled.p`
  font-size: 1.05rem;
  line-height: 1.6;
  color: #333;
  white-space: pre-line;
`;

const HomepageDescriptionManagement = () => {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const MAX_CHARS = 500;

  useEffect(() => {
    fetchHomepageData();
  }, []);

  const fetchHomepageData = async () => {
    try {
      const response = await fetch(`${API_URL}/homepage`);
      const data = await response.json();
      
      if (data.success) {
        setDescription(data.data.description || '');
      }
    } catch (error) {
      console.error('Error fetching homepage data:', error);
      setMessage({ type: 'error', text: 'Failed to load homepage data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!description.trim()) {
      setMessage({ type: 'error', text: 'Description cannot be empty' });
      return;
    }

    if (description.length > MAX_CHARS) {
      setMessage({ type: 'error', text: `Description exceeds maximum length of ${MAX_CHARS} characters` });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/homepage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ description })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Homepage description updated successfully!' });
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update description' });
      }
    } catch (error) {
      console.error('Error saving homepage data:', error);
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Card>
          <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
        </Card>
      </Container>
    );
  }

  const charCount = description.length;
  const isNearLimit = charCount > MAX_CHARS * 0.9;

  return (
    <Container>
      <Header>
        <Title>Homepage Description</Title>
        <Subtitle>Manage the description text displayed on the homepage</Subtitle>
      </Header>

      {message && (
        <Message type={message.type}>
          <FiInfo size={20} />
          {message.text}
        </Message>
      )}

      <Card>
        <FormGroup>
          <Label>Homepage Description</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter the homepage description text..."
            maxLength={MAX_CHARS}
          />
          <CharacterCount warning={isNearLimit}>
            {charCount} / {MAX_CHARS} characters
          </CharacterCount>
        </FormGroup>

        <SaveButton onClick={handleSave} disabled={saving || !description.trim()}>
          <FiSave />
          {saving ? 'Saving...' : 'Save Changes'}
        </SaveButton>
      </Card>

      <Card>
        <PreviewSection>
          <PreviewTitle>
            <FiInfo />
            Preview
          </PreviewTitle>
          <PreviewBox>
            <PreviewText>
              {description || 'Your description will appear here...'}
            </PreviewText>
          </PreviewBox>
        </PreviewSection>
      </Card>
    </Container>
  );
};

export default HomepageDescriptionManagement;
